import { LitElement, html } from 'lit-element';

import getPolyfilledDialog from '../utils/get-polyfilled-dialog';
import focusableElementSelectors from '../utils/focusable-elements';

import './icon';

const CLASS_DIALOG_CONTENT = 'js-content';
const CLASS_PRIMARY_CLOSE = 'js-primary-close';

const closeButton = html`
	<button class="button button--ghost button--icon-only js-close ${CLASS_PRIMARY_CLOSE}">
		<x-icon class="button__icon" icon="close" size="s"></x-icon>
	</button>`;

class Dialog extends LitElement {
	static get properties() {
		return {
			title: { type: String },
		};
	}

	constructor() {
		super();

		this.title = '';
		this.focusableElements = [];

		this.onDialogClose = this.onDialogClose.bind(this);
	}

	connectedCallback() {
		super.connectedCallback();

		this.addEventListener('click', this.onClick);
		this.addEventListener('keydown', this.onKeyDown);
		this.addEventListener('finish', this.close);
		this.addEventListener('updated', this.focusOnElement);
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		this.removeEventListener('keydown', this.onKeyDown);
		this.removeEventListener('finish', this.close);
		this.removeEventListener('updated', this.focusOnElement);

		super.connectedCallback();
	}

	async firstUpdated() {
		super.firstUpdated();

		this.dialog = getPolyfilledDialog(this);
		this.dialog.addEventListener('close', this.onDialogClose);

		this.open();
	}

	onClick({ target }) {
		const isClickedOutside = target.closest(`.${CLASS_DIALOG_CONTENT}`) === null;
		const isCloseClicked = target.classList.contains('js-close');

		if (isClickedOutside || isCloseClicked) {
			this.onDialogClose();
		}
	}

	onKeyDown(e) {
		this.trapFocus(e);
	}

	trapFocus(keyEvent) {
		const { key, shiftKey } = keyEvent;

		if (key !== 'Tab') {
			return;
		}

		if (!this.focusableElements.length) {
			return;
		}

		const [firstFocusable] = this.focusableElements;
		const lastFocusable = this.focusableElements[this.focusableElements.length - 1];

		// Shift + tab: focus previous element
		if (shiftKey && document.activeElement === firstFocusable) {
			lastFocusable.focus();

			keyEvent.preventDefault();
		// Tab: focus next
		} else if (!shiftKey && document.activeElement === lastFocusable) {
			firstFocusable.focus();
			keyEvent.preventDefault();
		}
	}

	onDialogClose() {
		this.remove();
	}

	open() {
		this.dialog.showModal();
	}

	close() {
		this.dialog.close();
	}

	remove() {
		this.parentNode.removeChild(this);
	}

	setBodyComponent(component) {
		this.bodyComponent = component;

		this.requestUpdate();
	}

	async updated() {
		// https://lit-element.polymer-project.org/guide/lifecycle#updatecomplete
		await this.updateComplete;

		this.focusableElements = [...this.querySelectorAll(focusableElementSelectors)];

		this.focusOnElement();
	}

	focusOnElement() {
		if (!this.focusableElements.length) {
			return;
		}

		// skip the primary close button of that's possible
		const focusElement = this.focusableElements.length > 1
			? this.focusableElements.find(el => !el.classList.contains(CLASS_PRIMARY_CLOSE))
			: this.focusableElements[0];

		focusElement.focus();
	}

	render() {
		return html`
			<dialog class="dialog">
				<div class="dialog__content ${CLASS_DIALOG_CONTENT}">
					<div class="dialog__header">
						<h1 class="dialog__title">${this.title}</h1>
						<div class="dialog__close">${closeButton}</div>
					</div>
					<div class="dialog__body js-dialog-body">
						${this.bodyComponent}
					</div>
				</div>
			</dialog>
		`;
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-dialog', Dialog);

export default Dialog;
